import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Keypair } from 'stellar-sdk';

type GeneratedKeypairRow = {
  index: number;
  publicKey: string;
  secret: string;
};

const MAX_BULK_COUNT = 500;

function buildCopyText(rows: GeneratedKeypairRow[]): string {
  const header = 'index, public_key, secret';
  const lines = rows.map((row) => `${row.index}, ${row.publicKey}, ${row.secret}`);
  return [header, ...lines].join('\n');
}

export default function WalletKeyGeneratorPage() {
  const [count, setCount] = useState<string>('10');
  const [rows, setRows] = useState<GeneratedKeypairRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied'>('idle');
  const [generating, setGenerating] = useState(false);

  const copyPayload = useMemo(() => buildCopyText(rows), [rows]);
  const hasGenerated = rows.length > 0;
  const hasCopied = copyStatus === 'copied';

  const handleGenerate = () => {
    setError(null);
    setCopyStatus('idle');

    const parsedCount = Number(count);
    if (!Number.isInteger(parsedCount) || parsedCount < 1 || parsedCount > MAX_BULK_COUNT) {
      setError(`Count must be an integer between 1 and ${MAX_BULK_COUNT}.`);
      return;
    }

    setGenerating(true);
    try {
      const generatedRows: GeneratedKeypairRow[] = [];
      for (let i = 1; i <= parsedCount; i += 1) {
        const kp = Keypair.random();
        generatedRows.push({
          index: i,
          publicKey: kp.publicKey(),
          secret: kp.secret(),
        });
      }
      setRows(generatedRows);
    } catch {
      setError('Failed to generate keypairs.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyAll = async () => {
    if (rows.length === 0) {
      setError('Generate keypairs before copying.');
      return;
    }

    setError(null);
    try {
      await navigator.clipboard.writeText(copyPayload);
      setCopyStatus('copied');
    } catch {
      setError('Copy failed. Please grant clipboard access and try again.');
    }
  };

  const handleClear = () => {
    setRows([]);
    setError(null);
    setCopyStatus('idle');
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Wallet Key Generator</Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Bulk-generate Pi wallet keypairs entirely in your browser.
        </Typography>
        <Alert
          severity="success"
          variant="outlined"
          sx={{ mb: 2, bgcolor: '#e8f5e9', borderColor: 'success.main' }}
        >
          <strong>Non-custodial:</strong> keys are generated on your browser and are NOT sent to the server. Save secrets securely, then clear this page.
        </Alert>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            type="number"
            size="small"
            label="Count"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            inputProps={{ min: 1, max: MAX_BULK_COUNT, step: 1 }}
            sx={{ width: 140 }}
          />
          <Button
            variant={hasGenerated ? 'outlined' : 'contained'}
            color={hasGenerated ? 'secondary' : 'primary'}
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? <CircularProgress size={20} /> : 'Generate'}
          </Button>
          <Button
            variant={hasCopied ? 'outlined' : hasGenerated ? 'contained' : 'outlined'}
            color={hasCopied ? 'secondary' : 'primary'}
            onClick={handleCopyAll}
            disabled={rows.length === 0}
          >
            {copyStatus === 'copied' ? 'Copied' : 'Copy all'}
          </Button>
          <Button
            variant={hasCopied ? 'contained' : 'text'}
            color={hasCopied ? 'primary' : 'inherit'}
            onClick={handleClear}
            disabled={rows.length === 0}
          >
            Clear
          </Button>
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Address (Public Key)</TableCell>
              <TableCell>Secret</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.index}>
                <TableCell>{row.index}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{row.publicKey}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{row.secret}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary">
                    No keypairs generated yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
